import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useGetMe, useLogout, getGetMeQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { Logo } from "./Logo";
import { AnnouncementBar } from "./marketing/AnnouncementBar";
import { FOOTER_TAGLINE } from "@/lib/marketing-copy";
import { useReveal } from "@/lib/use-reveal";
import { NoteWidget } from "@/components/notes/NoteWidget";

const ACCENT = "#1534FD";

interface NavItem {
  href: string;
  label: string;
  testid?: string;
  matchPaths: string[];
}

function useHash(): string {
  const [hash, setHash] = useState(() =>
    typeof window === "undefined" ? "" : window.location.hash,
  );
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener("hashchange", onChange);
    window.addEventListener("popstate", onChange);
    return () => {
      window.removeEventListener("hashchange", onChange);
      window.removeEventListener("popstate", onChange);
    };
  }, []);
  return hash;
}

function NavLink({
  item,
  location,
  setLocation,
}: {
  item: NavItem;
  location: string;
  setLocation: (path: string) => void;
}) {
  const currentHash = useHash();
  const hashIndex = item.href.indexOf("#");
  const isHashLink = hashIndex >= 0;
  const active = item.matchPaths.some((p) => {
    if (p === "/") return location === "/" && !currentHash;
    const hi = p.indexOf("#");
    if (hi >= 0) {
      const pPath = p.slice(0, hi) || "/";
      const pHash = p.slice(hi);
      return location === pPath && currentHash === pHash;
    }
    return location.startsWith(p);
  });
  const className =
    "relative inline-flex items-center text-[14px] font-medium text-foreground/75 hover:text-foreground transition-colors h-[72px]";
  const style = active
    ? { color: "#0a0a0a", boxShadow: `inset 0 -2px 0 0 ${ACCENT}` }
    : undefined;

  if (isHashLink) {
    const path = item.href.slice(0, hashIndex) || "/";
    const hash = item.href.slice(hashIndex + 1);
    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      e.preventDefault();
      const scrollToTarget = () => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          if (window.location.hash !== `#${hash}`) {
            window.history.replaceState(null, "", `#${hash}`);
          }
        }
      };
      if (location !== path) {
        setLocation(path);
        // Wait for the destination route to mount, then scroll.
        requestAnimationFrame(() => requestAnimationFrame(scrollToTarget));
      } else {
        scrollToTarget();
      }
    };
    return (
      <a
        href={item.href}
        data-testid={item.testid}
        data-active={active ? "true" : "false"}
        className={className}
        style={style}
        onClick={onClick}
      >
        {item.label}
      </a>
    );
  }

  return (
    <Link
      href={item.href}
      data-testid={item.testid}
      data-active={active ? "true" : "false"}
      className={className}
      style={style}
    >
      {item.label}
    </Link>
  );
}

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

const MARKETING_ROUTES = ["/", "/why-leadsweet", "/pricing"];

export function Layout({ children }: { children: React.ReactNode }) {
  const [location, setLocation] = useLocation();
  const { data: meData } = useGetMe();
  const user = meData?.user;
  const queryClient = useQueryClient();
  const scrolled = useScrolled(8);
  const isMarketing = MARKETING_ROUTES.includes(location);
  // Menu movil. La cabecera no tenia NINGUN tratamiento responsive: a 375px se
  // pintaba la navegacion de escritorio entera (264px), las dos columnas `1fr`
  // se quedaban a cero —el logo desaparecia, que es justo el defecto que el
  // comentario de mas abajo dice haber arreglado una vez— y los botones de
  // sesion se salian 84px, provocando scroll horizontal en TODAS las paginas.
  //
  // Esconder la navegacion sin mas dejaria un movil sin forma de moverse por el
  // sitio, asi que la navegacion se recoge aqui.
  const [menuAbierto, setMenuAbierto] = useState(false);
  useReveal([location]);
  useEffect(() => { setMenuAbierto(false); }, [location]);
  const logout = useLogout({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        setLocation("/");
      },
    },
  });

  const navItems: NavItem[] = [
    { href: "/why-leadsweet", label: "Why us", testid: "nav-why-us", matchPaths: ["/why-leadsweet"] },
    { href: "/discovery", label: "Discovery", matchPaths: ["/discovery"] },
    { href: "/#roi-calculator", label: "ROI", testid: "nav-roi", matchPaths: ["/#roi-calculator"] },
    ...(user
      ? [
          { href: "/lists", label: "Lists", matchPaths: ["/lists"] } as NavItem,
          // Lo desbloqueado (Daisy, 22-09; Tony, 21-10).
          { href: "/contacts", label: "My contacts", testid: "nav-my-contacts", matchPaths: ["/contacts"] } as NavItem,
        ]
      : []),
    // La bandeja de las notas ancladas, solo para quien entro por Amazify. Es
    // una herramienta interna: al resto ni se le nombra.
    ...(user?.auth_method === "amazify"
      ? [{ href: "/notes", label: "Notes", matchPaths: ["/notes"] } as NavItem]
      : []),
    { href: "/#pricing-preview", label: "Pricing", testid: "nav-pricing", matchPaths: ["/#pricing-preview"] },
  ];

  return (
    <div
      className={
        isMarketing
          ? "min-h-[100dvh] flex flex-col text-foreground font-sans ls-shell-dark"
          : "min-h-[100dvh] flex flex-col bg-background text-foreground font-sans"
      }
      data-route-theme={isMarketing ? "dark" : "light"}
    >
      {/* Salto al contenido para quien navega con teclado o lector de pantalla:
          sin esto hay que tabular por toda la cabecera en cada pagina. Estaba
          en la version desplegada y se perdio en la reconstruccion; se
          restaura con el mismo destino y las mismas clases. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[100] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:shadow-lg"
      >
        Skip to content
      </a>
      <AnnouncementBar />
      <header
        data-testid="site-header"
        data-scrolled={scrolled ? "true" : "false"}
        data-theme={isMarketing ? "dark" : "light"}
        className={
          isMarketing
            ? "ls-site-header ls-site-header-dark sticky top-9 z-50 h-[72px] transition-all"
            : "sticky top-9 z-50 h-[72px] bg-white transition-shadow"
        }
        style={
          isMarketing
            ? undefined
            : {
                boxShadow: scrolled
                  ? "0 1px 0 rgba(17,17,17,0.06), 0 4px 16px rgba(17,17,17,0.04)"
                  : "inset 0 -1px 0 rgba(17,17,17,0.06)",
              }
        }
      >
        <div className="h-full px-6 flex items-center justify-between gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:gap-6">
          <div className="flex items-center lg:justify-self-start">
            <Link
              href="/"
              data-testid="nav-logo"
              aria-label="LeadSweet, home"
              className="inline-flex items-center text-foreground"
            >
              {/* "From other pages, no way to Go back to homepage"
                  (LeadSweet Site - Notes.docx). El enlace SIEMPRE estuvo aqui y
                  apunta a "/": lo que fallaba es que no se veia. <Logo> por
                  defecto usa variant="dark", que es el wordmark PARA FONDOS
                  CLAROS, y en las rutas de marketing el header es
                  rgba(10,10,15,.72). Logo casi negro sobre fondo casi negro.
                  Ahora la variante sigue al tema de la cabecera. */}
              <Logo
                height={28}
                testId="nav-logo-wordmark"
                variant={isMarketing ? "light" : "dark"}
                decorative
              />
            </Link>
          </div>
          <nav className="hidden lg:flex items-center gap-8 lg:justify-self-center" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.href}
                item={item}
                location={location}
                setLocation={setLocation}
              />
            ))}
            {isMarketing ? (
              <button
                type="button"
                onClick={() => setLocation("/discovery")}
                className="ls-cmdk-hint"
                data-testid="nav-cmdk-hint"
                aria-label="Open search"
              >
                <span className="ls-cmdk-icon" aria-hidden="true">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <circle cx="4.5" cy="4.5" r="3.2" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M7 7L9.6 9.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="ls-cmdk-label">Search sellers</span>
                <span className="ls-cmdk-keys" aria-hidden="true">
                  <kbd>⌘</kbd>
                  <kbd>K</kbd>
                </span>
              </button>
            ) : null}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3 lg:justify-self-end">
            {user ? (
              <>
                <div
                  data-testid="nav-credits"
                  className="hidden sm:block text-[12px] font-mono uppercase tracking-[0.08em] text-secondary"
                >
                  {user.credits_left} credits
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link href="/pricing">Upgrade</Link>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden sm:inline-flex"
                  data-testid="nav-logout"
                  onClick={() => logout.mutate()}
                >
                  Log out
                </Button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  data-testid="nav-login"
                  className="hidden sm:inline-flex items-center text-[14px] font-medium text-foreground/75 hover:text-foreground transition-colors px-2"
                >
                  Log in
                </Link>
                <Link
                  href="/signup"
                  data-testid="nav-signup"
                  className="inline-flex items-center rounded-full bg-[#111111] text-white text-[14px] font-semibold px-5 py-2.5 hover:bg-[#1534FD] transition-colors"
                >
                  Sign up
                </Link>
              </>
            )}
            {/* Boton del menu movil. `lg:hidden` para que desaparezca en cuanto
                la navegacion de escritorio cabe. */}
            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md text-foreground/80 hover:text-foreground hover:bg-foreground/5 transition-colors"
              aria-expanded={menuAbierto}
              aria-controls="menu-movil"
              aria-label={menuAbierto ? "Close menu" : "Open menu"}
              data-testid="nav-menu-toggle"
              onClick={() => setMenuAbierto((v) => !v)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                {menuAbierto ? (
                  <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                ) : (
                  <path d="M3 6h14M3 10h14M3 14h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuAbierto ? (
          <div
            id="menu-movil"
            data-testid="nav-menu-movil"
            className={
              isMarketing
                ? "lg:hidden border-t border-white/10 bg-[#0A0A0F]/95 backdrop-blur-xl px-6 py-4 flex flex-col gap-1"
                : "lg:hidden border-t border-black/5 bg-white px-6 py-4 flex flex-col gap-1"
            }
          >
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="py-2.5 text-[15px] font-medium text-foreground/85 hover:text-foreground"
                onClick={() => setMenuAbierto(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-2 pt-3 border-t border-current/10 flex flex-col gap-1">
              {user ? (
                <>
                  <span className="py-1 text-[12px] font-mono uppercase tracking-[0.08em] text-secondary">
                    {user.credits_left} credits
                  </span>
                  <button
                    type="button"
                    className="py-2.5 text-left text-[15px] font-medium text-foreground/85 hover:text-foreground"
                    onClick={() => { setMenuAbierto(false); logout.mutate(); }}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  className="py-2.5 text-[15px] font-medium text-foreground/85 hover:text-foreground"
                  onClick={() => setMenuAbierto(false)}
                >
                  Log in
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </header>
      <main id="main-content" className="flex-1 flex flex-col">{children}</main>
      {isMarketing ? (
        <footer
          className="ls-footer-x"
          data-testid="site-footer"
        >
          <div className="ls-footer-x-inner">
            <div className="ls-footer-x-top">
              <div className="ls-footer-x-brand">
                {/* Mismo caso que la cabecera: este footer es el de marketing
                    (#0D0E13), asi que necesita el wordmark claro. */}
                <Logo height={28} testId="footer-logo-wordmark" variant="light" />
                <p className="ls-footer-x-tag">
                  Verified Amazon seller intelligence for GTM teams.
                </p>
                <div className="ls-footer-x-social" aria-label="Social links">
                  <a
                    href="https://www.linkedin.com/company/leadsweet"
                    aria-label="LeadSweet on LinkedIn"
                    className="ls-footer-x-social-link"
                    data-testid="footer-social-linkedin"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M3.6 5.2h2.2v7.2H3.6V5.2zm1.1-3.4a1.3 1.3 0 110 2.6 1.3 1.3 0 010-2.6zM7.4 5.2h2.1v1h.03c.3-.55 1.03-1.13 2.12-1.13 2.27 0 2.69 1.49 2.69 3.43v3.9H12.2V8.94c0-.84-.02-1.93-1.18-1.93s-1.36.92-1.36 1.87v3.52H7.4V5.2z"/>
                    </svg>
                  </a>
                  <a
                    href="https://x.com/leadsweet"
                    aria-label="LeadSweet on X"
                    className="ls-footer-x-social-link"
                    data-testid="footer-social-x"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                      <path d="M10.6 1.5h2.05L8.2 6.6l5.25 6.9H9.34l-3.22-4.21-3.69 4.21H.38l4.78-5.47L.13 1.5h4.2l2.91 3.85L10.6 1.5zm-.72 10.7h1.14L4.18 2.69H2.96l6.92 9.51z"/>
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/leadsweet"
                    aria-label="LeadSweet on Facebook"
                    className="ls-footer-x-social-link"
                    data-testid="footer-social-facebook"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
                      <path d="M8.3 13.5V7.8h1.93l.29-2.24H8.3V4.12c0-.65.18-1.09 1.11-1.09h1.18V1.04c-.2-.03-.9-.09-1.72-.09-1.7 0-2.87 1.04-2.87 2.95v1.66H4.07v2.24h1.93v5.7H8.3z"/>
                    </svg>
                  </a>
                </div>
              </div>
              <div className="ls-footer-x-cols">
                <div className="ls-footer-x-col">
                  <div className="ls-footer-x-col-head">Product</div>
                  <Link href="/why-leadsweet" className="ls-footer-x-link">Why us</Link>
                  <Link href="/discovery" className="ls-footer-x-link">Discovery</Link>
                  <Link href="/pricing" className="ls-footer-x-link">Pricing</Link>
                  <a href="/#roi-calculator" className="ls-footer-x-link">ROI Calculator</a>
                </div>
                <div className="ls-footer-x-col">
                  <div className="ls-footer-x-col-head">Company</div>
                  <a href="#" className="ls-footer-x-link">About</a>
                  <a href="#" className="ls-footer-x-link">Careers</a>
                  <a href="#" className="ls-footer-x-link">Trust Center</a>
                  <a href="mailto:hello@leadsweet.com" className="ls-footer-x-link">Contact</a>
                </div>
                <div className="ls-footer-x-col">
                  <div className="ls-footer-x-col-head">Resources</div>
                  <a href="#" className="ls-footer-x-link">Blog</a>
                  <a href="#" className="ls-footer-x-link">Help Center</a>
                  <a href="#" className="ls-footer-x-link">API Docs</a>
                  <a href="#" className="ls-footer-x-link">Status</a>
                </div>
                <div className="ls-footer-x-col">
                  <div className="ls-footer-x-col-head">Account</div>
                  <Link href="/login" className="ls-footer-x-link">Log In</Link>
                  <Link href="/signup" className="ls-footer-x-link">Sign Up</Link>
                </div>
              </div>
            </div>
            <div className="ls-footer-x-bottom">
              <div className="ls-footer-x-copy">
                © {new Date().getFullYear()} LeadSweet. All rights reserved.
              </div>
              <div className="ls-footer-x-legal">
                <a href="#" className="ls-footer-x-link-sm">Privacy Policy</a>
                <span className="ls-footer-x-sep" aria-hidden="true">|</span>
                <a href="#" className="ls-footer-x-link-sm">Terms of Service</a>
                <span className="ls-footer-x-sep" aria-hidden="true">|</span>
                <a href="#" className="ls-footer-x-link-sm">GDPR</a>
              </div>
            </div>
          </div>
        </footer>
      ) : (
        <footer
          className="border-t border-border bg-white px-6 py-8"
          data-testid="site-footer"
        >
          <div className="max-w-[1280px] mx-auto flex flex-col gap-4">
            <p
              className="text-[15px] font-semibold tracking-tight text-foreground max-w-[640px]"
              data-testid="footer-tagline"
            >
              {FOOTER_TAGLINE}
            </p>
            <div className="flex items-center justify-between flex-wrap gap-3 pt-4 border-t border-border/60">
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                <Logo height={22} testId="footer-logo-wordmark" />
                <span aria-hidden="true">·</span>
                <span>© {new Date().getFullYear()} · All rights reserved</span>
              </div>
              <div className="flex items-center gap-5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                <Link href="/why-leadsweet" className="hover:text-foreground transition-colors">
                  Why us
                </Link>
                <Link href="/discovery" className="hover:text-foreground transition-colors">
                  Discovery
                </Link>
                <Link href="/pricing" className="hover:text-foreground transition-colors">
                  Pricing
                </Link>
              </div>
            </div>
          </div>
        </footer>
      )}

      {/* Notas ancladas: se monta UNA vez, aqui, y solo si esta sesion entro por
          "Login with Amazify". El servidor vuelve a comprobarlo en cada
          endpoint — esconder el widget no protege el dato. */}
      {user?.auth_method === "amazify" && <NoteWidget />}
    </div>
  );
}
